import {AsyncPipe, DecimalPipe, PercentPipe} from '@angular/common'
import {Component, inject, OnInit, output} from '@angular/core'
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms'
import {MatButton, MatIconButton} from '@angular/material/button'
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {FormatNumberDirective, getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {debounceTime, filter} from 'rxjs/operators'
import {BuildingTypes, IKalpBuilding, KalpBuilding} from '../../model/kalp-building'
import {DataService, SaveData} from '../../services/data.service'
import {KalpService, Scenarios} from '../../services/kalp.service'
import {NGService} from '../../services/ng.service'
import {PropertyComponent} from './property/property.component'

@Component({
  selector: 'spb-living',
  templateUrl: './living.component.html',
  styleUrls: ['./living.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    PropertyComponent,
    MatButton,
    AsyncPipe,
    FormatNumberDirective,
    DecimalPipe,
    MatIcon,
    MatIconButton,
    MatError,
    PercentPipe
  ]
})
export class LivingComponent implements OnInit {
  public dataService = inject(DataService)
  public ngService = inject(NGService)
  public stepValid = output<boolean>()

  public scenario: Scenarios = Scenarios.Purchase

  public buildings: KalpBuilding[] = []

  public valid = false

  public seekedAmount =
    new FormControl<number>(0, {
      nonNullable: true,
      validators: [Validators.required]
    })

  /**
   * Loan limits configuration
   */
  public readonly limitStandard = 0.9
  public readonly limitIncrease = 0.8

  private readonly kalpService = inject(KalpService)

  /**
   * Simple export to the template.
   */
  public scenarioTypes = [Scenarios.Purchase, Scenarios.Increase, Scenarios.Conditions, Scenarios.Transfer]

  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  /**
   * Get the current limit percentage based on scenario
   */
  get currentLimit(): number {
    return this.scenario === Scenarios.Increase ? this.limitIncrease : this.limitStandard
  }

  /**
   * Calculates the maximum allowed loan amount based on the scenario.
   * Purchase/Transfer: 90% of property value
   * Increase: 80% of property value MINUS existing mortgageRequirementDebt
   */
  get maxLoanAmount(): number {
    const primary = this.buildings.find(b => b.primary)

    if (!primary) {
      return 0
    }

    const maxTotalDebt = (primary.mortgageRequirementValue || 0) * this.currentLimit

    // For Increase scenario: max new loan = 80% of value - existing debt
    if (this.scenario === Scenarios.Increase) {
      const existingDebt = primary.mortgageRequirementDebt || 0
      return Math.max(0, maxTotalDebt - existingDebt)
    }

    return maxTotalDebt
  }

  ngOnInit(): void {
    this.seekedAmount.addValidators(this.loanCapValidator())

    this.kalpService.scenarioChange$
      .pipe(
        this.takeUntilDestroyed(),
        filter(Boolean)
      )
      .subscribe((scenario: Scenarios) => {
        this.scenario = scenario
        this.buildings = []
        if (scenario === Scenarios.Purchase) {
          this.buildings.push(
            new KalpBuilding('Nya bostaden', true, BuildingTypes.purchase),
            new KalpBuilding('Nuvarande boende som lämnas', false, BuildingTypes.leave)
          )
          this.buildings[1].valid = true
        } else {
          this.buildings.push(new KalpBuilding('Uppgifter om bostaden', true, BuildingTypes.keep))
        }
        if (scenario === Scenarios.Conditions) {
          this.seekedAmount.clearValidators()
          this.seekedAmount.updateValueAndValidity()
        }
        this.kalpService.buildings$.next(this.buildings)

        // Re-validate seeked amount when scenario changes as limits might change
        this.seekedAmount.updateValueAndValidity()
        this.setValidity()
      })

    this.seekedAmount.valueChanges
      .pipe(
        debounceTime(300),
        this.takeUntilDestroyed()
      )
      .subscribe((val: number) => {
        this.kalpService.loanAmount$.next(val)
        this.setValidity()
      })

    this.dataService.data$
      .pipe(
        this.takeUntilDestroyed()
      )
      .subscribe((value: SaveData | null) => {
        if (value) {
          // Set scenario first so validator works correctly
          this.scenario = value.scenario
          this.buildings = value.livings.buildings.map((building: IKalpBuilding) => {
            const b = new KalpBuilding()
            b.setFromInput(building)
            return b
          })
          // Set seekedAmount after buildings so validator has correct data
          this.seekedAmount.setValue(value.livings.seekedAmount ?? 0)
          this.seekedAmount.markAsTouched()
          this.seekedAmount.markAsDirty()
          this.seekedAmount.updateValueAndValidity()
          this.kalpService.buildings$.next(this.buildings)
        } else {
          this.seekedAmount.setValue(0)
        }
      })
  }

  public addBuilding(): void {
    this.buildings.push(new KalpBuilding('Övrigt boende', false, BuildingTypes.other))
    this.buildings.at(-1)!.valid = true
    this.setValidity()
  }

  public removeBuilding(index: number): void {
    this.buildings.splice(index, 1)
    this.setValidity()
  }

  public buildingChanges(building: KalpBuilding): void {
    const b = this.buildings.find((b1: KalpBuilding) => b1.id === building.id)
    if (!b) {
      return
    }
    Object.assign(b, building)

    // Re-validate seeked amount if primary building value changes
    if (b.primary) {
      this.seekedAmount.updateValueAndValidity()
    }

    this.setValidity()
  }

  private setValidity(): void {
    this.valid = this.buildings.reduce((v: boolean, building: KalpBuilding) => v && building.valid && this.seekedAmount.valid, true)
    this.stepValid.emit(this.valid)
    // this.controlService.setStepValidity(3, this.valid)
    if (this.valid) {
      this.kalpService.buildings$.next(this.buildings)
    }
  }

  private loanCapValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const hasValue = !!this.buildings.find(b => b.primary)?.mortgageRequirementValue
      const isIncrease = this.scenario === Scenarios.Increase || this.scenario === Scenarios.Purchase

      // Only validate for 'Increase' scenario when property value is available
      if (!isIncrease || !hasValue) {
        return null
      }

      const maxLoan = this.maxLoanAmount
      const isExceeded = control.value > 0 && control.value > maxLoan

      // Return error details if requested amount exceeds calculated max loan
      return isExceeded
        ? {maxLoanExceeded: {max: maxLoan, limit: this.currentLimit}}
        : null
    }
  }
}
